import { type ChangeEvent, useContext, useEffect, useState } from "react";
import { useCookies } from "react-cookie";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

import { Button } from "@/app/components/ui/button";
import type { ReferenceItem, ResumeContextValue } from "@/app/types/resume";

import tips from "../../../../../assets/copy/tips";
import AutoSuggestion from "../../../../../components/AutoSuggestion";
import ErrorToast from "../../../../../components/ErrorToast";
import Spinner from "../../../../../components/Spinner";
import Tips from "../../../../../components/Tips";
import InputField from "../../../../../components/UIComponents/InputField";
import SelectField from "../../../../../components/UIComponents/SelectField";
import ResumeContext from "../../../../../context/ResumeContext";
import { resumeObjectToContextFormat } from "../../../../../handlers/valueTransformation";
import { Reference } from "../../../../../models/resume/reference";
import { referenceSchema } from "../../../../../schemas/resume/reference";
import { StaticValue } from "../../../../../seed/constants";
import options from "../../../../../seed/dropDownOptions";
import mapping from "../../../../../seed/metadata/filemapping";
import { usePatchResumeSection } from "../../../../../services/create-resume/queries";
import { RESUME_SECTIONS } from "../../../../../services/create-resume/resumeSections";

interface ReferenceReducerState {
	referenceId: string;
	fullName: string;
	designation: string;
	workPlace: string;
	phoneNumber: string;
	emailAddress: string;
	workplaceLocation: string;
	country?: string;
}

interface RefInfoState {
	id: string;
	name: string;
	designation: string;
	workPlace: string;
	phoneNumber: string;
	email: string;
}

interface ReferenceErrors {
	name: string;
	designation: string;
	workPlace: string;
	address: string;
	phoneNumber: string;
	email: string;
}

interface PatchError {
	response: { data: { message: string | string[] } };
}

const ReferenceInformation = () => {
	const [cookies] = useCookies(["resume"]);
	const navigate = useNavigate();
	const reference = useSelector((state: { ReferenceInfoReducer: ReferenceReducerState }) => state.ReferenceInfoReducer);
	const patchSection = usePatchResumeSection();
	const staticValue = new StaticValue();
	const staticValues = staticValue.getDefaultValues();
	const [serverErrors, setServerErrors] = useState<string[]>([]);
	const [address, setAddress] = useState(reference.workplaceLocation);
	const [country, setCountry] = useState(reference.country || "Tanzania");
	const locations: string[] = mapping["districtsRegions"][0].districtRegions;

	const navigationState = useLocation().state as { referenceId: string } | null;

	// The legacy select relied on the HTML fallback (an <option> without a value
	// uses its text). An explicit value keeps the same submitted string.
	const countryOptions = options.phoneCountryCodeOptions.map((option: { name: string }) => ({
		value: option.name,
		name: option.name,
	}));

	//Declaring the form states
	const [refInfo, setRefInfo] = useState<RefInfoState>({
		id: reference.referenceId,
		name: reference.fullName,
		designation: reference.designation,
		workPlace: reference.workPlace,
		phoneNumber: reference.phoneNumber,
		email: reference.emailAddress,
	});

	const { resumeData, setResumeData } = useContext(ResumeContext) as ResumeContextValue;
	const { references } = resumeData.extras;

	useEffect(() => {
		if (navigationState) {
			const data = references.filter((item) => item.id === navigationState.referenceId)[0] as ReferenceItem | undefined;
			setRefInfo(() => {
				return data as unknown as RefInfoState;
			});

			if (references.length !== 0 && data) {
				setAddress(() => {
					return data.address;
				});
				setCountry(() => {
					return data.country;
				});
			}
		}
	}, [resumeData]);

	// The form event handler
	const onChangeInput = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
		if (e.target.name === "address") {
			setAddress(e.target.value);
		} else if (e.target.name === "country") {
			setCountry(e.target.value);
		} else {
			setRefInfo((prevState) => {
				return {
					...prevState,
					[e.target.name]: e.target.value,
				};
			});
		}
	};

	const [errors, setErrors] = useState<ReferenceErrors>({
		name: "",
		designation: "",
		workPlace: "",
		address: "",
		phoneNumber: "",
		email: "",
	});

	const { name, designation, workPlace, phoneNumber, email } = refInfo;
	const formData = {
		name,
		designation,
		workPlace,
		address,
		phoneNumber,
		email,
	};

	//form submission handler
	const addReference = () => {
		const validation = referenceSchema.safeParse({ ...formData, country });
		if (!validation.success) {
			const fieldErrors = validation.error.flatten().fieldErrors;
			setErrors({
				name: fieldErrors.name?.[0] || "",
				designation: fieldErrors.designation?.[0] || "",
				workPlace: fieldErrors.workPlace?.[0] || "",
				address: fieldErrors.address?.[0] || "",
				phoneNumber: fieldErrors.phoneNumber?.[0] || "",
				email: fieldErrors.email?.[0] || "",
			});
			return;
		}
		setErrors({ name: "", designation: "", workPlace: "", address: "", phoneNumber: "", email: "" });

		const [city, region] = formData.address.split(",");

		const payload = {
			city: city.trim(),
			region: (region || "").trim(),
			country: country,
			name: formData.name,
			email: formData.email,
			designation: formData.designation,
			workPlace: formData.workPlace,
			phoneNumber: formData.phoneNumber,
		};

		const refInfoPayload = new Reference(payload);
		const refReq = refInfoPayload.getReferenceRequest();

		if (refInfo.id !== "") {
			refReq.referenceId = refInfo.id;
		}

		const resumeId = cookies[staticValues.localStorageResumeId];

		patchSection.mutate({ resumeId, section: RESUME_SECTIONS.REFERENCE, data: refReq } as never, {
			onSuccess,
			onError,
		});
	};

	function onSuccess(ledger: Record<string, unknown>) {
		resumeObjectToContextFormat(ledger, setResumeData);
		navigate("/resume/reference-summary");
	}

	function onError(error: Error) {
		const serverResponse = (error as unknown as PatchError).response.data;
		Array.isArray(serverResponse.message)
			? setServerErrors((prev) => {
					return [...prev, ...serverResponse.message];
				})
			: setServerErrors((prev) => {
					return [...prev, serverResponse.message as string];
				});
	}

	return (
		<div className="min-h-screen">
			{serverErrors.map((item, index) => {
				return <ErrorToast error={item} key={index} />;
			})}
			<div className="w-full md:max-w-xl">
				<div className="mb-3 flex items-start justify-between gap-3 text-gray-900">
					<p>Enter the details of your referee.</p>
					<Tips content={tips.referees} />
				</div>

				<div className="mb-4">
					<InputField
						type="text"
						label="Name"
						name="name"
						value={refInfo.name}
						onChange={onChangeInput}
						error={errors.name}
						placeholder="Add name..."
					/>
				</div>
				<div className="mb-4">
					<InputField
						type="text"
						label="Designation"
						name="designation"
						value={refInfo.designation}
						onChange={onChangeInput}
						error={errors.designation}
						placeholder="Add Designation..."
					/>
				</div>
				<div className="mb-4">
					<InputField
						type="text"
						label="Workplace"
						name="workPlace"
						value={refInfo.workPlace}
						onChange={onChangeInput}
						error={errors.workPlace}
						placeholder="Add reference&#39;s company name..."
					/>
				</div>

				<div className="mb-4 grid grid-cols-2 gap-3">
					<SelectField
						name="country"
						value={country}
						onChange={onChangeInput}
						options={countryOptions}
						label="Country"
					/>

					<div className="relative">
						{!(country === "Tanzania") && (
							<InputField
								type="text"
								label="District/Region"
								name="address"
								value={address}
								onChange={onChangeInput}
								placeholder="Enter Address "
								error={errors.address}
							/>
						)}
						{country === "Tanzania" && (
							<AutoSuggestion
								suggestions={locations}
								label="District/Region"
								type="text"
								name="address"
								userInput={address}
								setUserInput={setAddress}
								isForm={true}
								error={errors.address}
							/>
						)}
					</div>
				</div>

				<div className="mb-4">
					<InputField
						type="text"
						label="Phone number (Eg. +255788000000)"
						name="phoneNumber"
						value={refInfo.phoneNumber}
						onChange={onChangeInput}
						error={errors.phoneNumber}
						placeholder="Enter Reference&#39;s phone number E.g. +255767000000"
					/>
				</div>
				<div className="mb-4">
					<InputField
						type="email"
						label="Email address"
						name="email"
						value={refInfo.email}
						onChange={onChangeInput}
						error={errors.email}
						placeholder="Enter Reference&#39;s Email Address"
					/>
				</div>

				<div className="mt-4 flex items-center justify-end gap-2">
					<Button
						type="button"
						variant="ghost"
						onClick={() => {
							navigate(-1);
						}}
					>
						Skip for now
					</Button>
					<Button type="button" onClick={addReference}>
						{patchSection.isPending ? (
							<>
								Loading <Spinner />
							</>
						) : (
							"Save and Continue"
						)}
					</Button>
				</div>
			</div>
		</div>
	);
};

export default ReferenceInformation;
